import { classifyCargo, genderKey } from '../pessoal/jobArchetypes';
import { IPessoalEmpregado } from '../../types';
import { IsoGender, skinFor } from './IsoCharacter';
import { mulberry32, seedFrom } from './rng';

export type IsoStatus = 'ativo' | 'demitido' | 'afastado' | 'outro';

export interface IsoPerson {
  id: string;
  cargo: string;
  archetypeKey: string;
  color: string;
  gender: IsoGender;
  skin: string;
  nome?: string;
  anos: number;
  status: IsoStatus;
}

interface PorCargo {
  cargo: string;
  ativos: number;
  demitidos: number;
}
interface PorGenero {
  sexo: string;
  qtd: number;
}

/** Anos de casa a partir de 'YYYY-MM-DD'. */
const yearsSince = (d?: string | null): number => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d ?? '');
  if (!m) return 0;
  try {
    const then = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
    return Math.max(0, (Date.now() - then) / (365.25 * 24 * 3600 * 1000));
  } catch {
    return 0;
  }
};

const statusDe = (e: IPessoalEmpregado): IsoStatus => {
  if (e.data_demissao) return 'demitido';
  const s = (e.status ?? '').toLowerCase();
  if (s.includes('afast') || s.includes('licen') || s.includes('feri')) return 'afastado';
  return 'ativo';
};

/** Pessoas a partir da LISTA REAL de empregados (Pessoal) — identidade individual. */
export function peopleFromEmpregados(emp: IPessoalEmpregado[]): IsoPerson[] {
  return emp
    .filter(e => !e.data_demissao)
    .map((e, i) => {
      const arq = classifyCargo(e.cargo);
      return {
        id: `e${e.empregado_id}`,
        cargo: e.cargo,
        archetypeKey: arq.key,
        color: arq.accent,
        gender: genderKey(e.sexo),
        skin: skinFor(e.empregado_id || i * 7 + 3),
        nome: e.nome,
        anos: yearsSince(e.admissao),
        status: statusDe(e),
      };
    });
}

/** Distribui gêneros (por_genero) proporcionalmente — estável. */
function quotaGeneros(porGenero: PorGenero[], n: number): IsoGender[] {
  const total = porGenero.reduce((s, g) => s + g.qtd, 0);
  if (!total) return Array.from({ length: n }, () => 'N' as IsoGender);
  const fila: IsoGender[] = [];
  porGenero.forEach(g => {
    const k = genderKey(g.sexo);
    const qt = Math.round((g.qtd / total) * n);
    for (let i = 0; i < qt; i += 1) fila.push(k);
  });
  while (fila.length < n) fila.push('N');
  return fila;
}

/** Pessoas SINTETIZADAS a partir de agregados (Dossiê só tem por_cargo/por_genero). */
export function peopleFromAggregates(
  porCargo: PorCargo[],
  porGenero: PorGenero[],
  seed = 1,
): IsoPerson[] {
  const lista: IsoPerson[] = [];
  const ordenado = [...porCargo].sort((a, b) => b.ativos - a.ativos);
  const total = ordenado.reduce((s, p) => s + p.ativos, 0);
  const gen = quotaGeneros(porGenero, total);
  const rnd = mulberry32(seedFrom(seed, 11));
  let gi = 0;
  ordenado.forEach(pc => {
    const arq = classifyCargo(pc.cargo);
    for (let i = 0; i < pc.ativos; i += 1) {
      lista.push({
        id: `${arq.key}#${i}`,
        cargo: pc.cargo,
        archetypeKey: arq.key,
        color: arq.accent,
        gender: gen[gi] ?? 'N',
        skin: skinFor(Math.floor(rnd() * 9999)),
        anos: 0,
        status: 'ativo',
      });
      gi += 1;
    }
  });
  return lista;
}
